import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, downloadFile } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Download, Table, Loader2 } from 'lucide-react';
import { useMembership } from '@/hooks/useMembership';

type DataType = 'water_readings' | 'feedings' | 'samplings';
type FormatType = 'raw' | 'summarized';

const dataTypeLabels: Record<DataType, string> = {
  water_readings: 'Water Readings',
  feedings: 'Feedings',
  samplings: 'Samplings',
};

const formatLabels: Record<FormatType, { label: string; description: string }> = {
  raw: {
    label: 'Raw Data',
    description: 'All fields, one row per reading',
  },
  summarized: {
    label: 'Summarized',
    description: 'Daily aggregates with min/max/avg',
  },
};

export function DataExportCard() {
  const { membership } = useMembership();
  const farmId = membership?.farmId;

  const [dataType, setDataType] = useState<DataType>('water_readings');
  const [format, setFormat] = useState<FormatType>('raw');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Export mutation
  const exportData = useMutation({
    mutationFn: async () => {
      const params: Record<string, string> = {
        type: dataType,
        format,
      };

      if (dateFrom) {
        params.from = dateFrom;
      }
      if (dateTo) {
        params.to = dateTo;
      }

      const blob = await api.get<Blob>(`/api/v1/farms/${farmId}/reports/export`, {
        params,
        responseType: 'blob',
      });

      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${dataType}-${format}-${dateStr}.xlsx`;
      downloadFile(blob, filename);
    },
  });

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start gap-4">
          <div className="p-3 bg-green-100 rounded-lg shrink-0">
            <Table className="h-6 w-6 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">Excel Export</h3>
            <p className="text-sm text-gray-500 mt-1">
              Export farm data to Excel for analysis, record-keeping, or sharing with partners.
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          {/* Data Type */}
          <Select
            label="Data Type"
            value={dataType}
            onChange={(e) => setDataType(e.target.value as DataType)}
            options={Object.entries(dataTypeLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />

          {/* Format */}
          <Select
            label="Format"
            value={format}
            onChange={(e) => setFormat(e.target.value as FormatType)}
            description={formatLabels[format].description}
            options={Object.entries(formatLabels).map(([value, { label }]) => ({
              value,
              label,
            }))}
          />

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="From Date"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              label="To Date"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          {!dateFrom && !dateTo && (
            <p className="text-xs text-gray-500">
              Leave date fields empty to export all data.
            </p>
          )}

          {/* Export Button */}
          <Button
            onClick={() => exportData.mutate()}
            disabled={exportData.isPending}
            variant="outline"
            className="w-full"
          >
            {exportData.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export to Excel
              </>
            )}
          </Button>

          {exportData.isError && (
            <p className="text-sm text-status-critical">
              Failed to export data. Please try again.
            </p>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
